import { Box } from '@mui/material';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import * as React from 'react';
import type { Color as TColor } from '~/services/api-utils.server';
import { ColorTooltipText } from '../ProductDetails/Attributes';

export default function Colors({
  colors,
  selectedColor,
  disabled,
  disabledLabel,
  onColorChange,
  unavailableColors,
  isDesignLabSummary,
  unavailableInventoryParts,
}: {
  colors: TColor[];
  selectedColor?: string;
  disabled?: boolean;
  disabledLabel?: string;
  onColorChange?: React.MouseEventHandler<HTMLButtonElement>;
  unavailableColors?: string[];
  isDesignLabSummary?: boolean;
  unavailableInventoryParts?: string[];
}) {
  const theme = useTheme();
  return (
    <>
      <Typography variant="subtitle2" color="text.secondary" alignSelf="flex-start" flex='0 0 auto' mr={1.5}>
        Color:
      </Typography>
      <Stack direction="row" alignItems="center" flexWrap="wrap">
        {colors.map((color: TColor) => {
          const isUnavailable = unavailableColors?.includes(color.name);
          const unavailableColorInventory = unavailableInventoryParts?.filter((variant) =>
            variant.includes(color.name)
          );

          const selectedBorder =
            selectedColor === color.name
              ? '1px solid rgba(0, 0, 0, 1)'
              : isUnavailable
              ? `1px dashed ${theme.palette.text.disabled}`
              : '1px solid rgba(0, 0, 0, 0)';
          return (
            <Color
              key={color.hex}
              color={color}
              border={selectedBorder}
              disabled={disabled}
              disabledLabel={
                disabledLabel ? (
                  disabledLabel
                ) : (
                  <ColorTooltipText colorName={color.name} unavailableInventory={unavailableColorInventory} />
                )
              }
              onColorChange={color.name === selectedColor ? undefined : onColorChange}
              noStockAvailable={(unavailableColorInventory?.length ?? 0) > 0}
              isSelected={color.name === selectedColor}
            />
          );
        })}
      </Stack>
    </>
  );
}

export function Color({
  color,
  border,
  disabled,
  disabledLabel,
  onColorChange,
  readOnly,
  isUnavailable,
  noStockAvailable,
  isSelected,
}: {
  color: TColor;
  border?: string;
  disabled?: boolean;
  disabledLabel?: string | JSX.Element;
  onColorChange?: React.MouseEventHandler<HTMLButtonElement>;
  readOnly?: boolean;
  isUnavailable?: boolean;
  noStockAvailable?: boolean;
  isSelected?: boolean;
}) {
  return (
    <Tooltip title={disabled || noStockAvailable ? disabledLabel : color.name} placement="top" arrow>
      {readOnly ? (
        <Box
          width={20}
          height={20}
          borderRadius={20}
          border="1px solid rgba(0, 0, 0, .12)"
          bgcolor={`#${color.hex}`}
          mr={1.25}
          position='relative'
          sx={[
            Boolean(isUnavailable || noStockAvailable) && {
              outline: '1.5px dashed rgba(158, 158, 158, 1)',
              outlineOffset: '3px',
            },
            Boolean(noStockAvailable) && {
              '&:before': {
                content: "''",
                position: 'absolute',
                left: -5,
                right: -5,
                top: '50%',
                borderTop: '1px solid',
                borderColor: 'rgba(158, 158, 158, 1)',
                transform: 'rotate(-40deg)',
                pointerEvents: 'none',
              },
            },
          ]}
        />
      ) : (
        <Stack borderRadius="50%" border={!noStockAvailable ? border : '1px solid transparent'} p={0.25} mr={0.5}>
          <Button
            sx={[
              {
                minWidth: 20,
                height: 20,
                backgroundColor: `#${color.hex}`,
                borderRadius: '50%',
                border: '1px solid rgba(0, 0, 0, .12)',
                '&:hover': { backgroundColor: `#${color.hex}` },
              },
              Boolean(isUnavailable || noStockAvailable) &&
                Boolean(isSelected) && {
                  outline: '1.5px dashed rgba(158, 158, 158, 1)',
                  outlineOffset: '3px',
                },
              Boolean(noStockAvailable) &&
                Boolean(isSelected) && {
                  '&:before': {
                    content: "''",
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    top: '45%',
                    borderTop: '1px solid',
                    borderColor: 'rgba(158, 158, 158, 1)',
                    transform: 'rotate(-45deg)',
                    pointerEvents: 'none',
                  },
                },
            ]}
            value={color.name}
            disabled={disabled}
            onClick={onColorChange}
          ></Button>
        </Stack>
      )}
    </Tooltip>
  );
}

export function SelectedColor({ colors, selectedColor }: { colors: TColor[]; selectedColor: string }) {
  const _selectedColor = colors.find((color) => color.name === selectedColor);

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Typography variant="subtitle2" color="text.secondary" alignSelf="baseline" mr={1.5}>
        Color:
      </Typography>
      <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1} sx={{ '.MuiBox-root': { mr: 0 } }}>
        <Color color={_selectedColor as TColor} readOnly />
      </Stack>
    </Stack>
  );
}
